import axios from 'axios'
import { cloneDeep } from 'lodash-es';
import ISO6391 from 'iso-639-1'

import {url} from "@/url";
import {oqlForUrl} from "@/oqlSerialize";
import {createDisplayFilter, createSimpleFilter, filtersAsUrlStr} from "@/filterConfigs";
import {openAlexCountries} from "@/countries";
import {isSmallVocabType, searchVocab} from "@/vocabAutocomplete";
import {getFacetConfig} from "@/facetConfigUtils";
import {collectionMatchType, filterCollectionsForField} from "@/collectionFilter";
import {openAlexSdgs} from "@/sdgs";
import {isListedInKey, listedInLabel} from "@/listedIn";
import {getEntityConfig} from "@/entityConfigs";
import {startingSetOqo} from "@/oqlPipeline";
import {urlBase, axiosConfig, DISABLE_SERVER_CACHE} from "@/apiConfig";
import store from "@/store";
import {edgeOn, edgeSupports, edgeRows} from "@/edgeAutocomplete";
import {isServerRender} from "@/utils/env";
import {withRefusalPolicy} from "@/requestRefusal";

// Response cache for this browser tab. Never used on the server (#1689): a
// module-level cache there would be shared by every visitor and grow forever.
const cache = {};

const getFromCache = function (url) {
    if (isServerRender || !cache[url]) { return; }
    return cloneDeep(cache[url]);
}

const stockCache = function (url, ret) {
    if (isServerRender) { return; }
    cache[url] = cloneDeep(ret);
}

// #1689: this module never calls the API during a server render. Record pages get
// their data from the Nitro record middleware (server/middleware/02.records.ts);
// anything else (group-bys, counts, display-name lookups) is the browser's job, so
// on the server the call stays pending, exactly like the browser's first render
// before its fetch returns, and the hydrated page then fetches it for real.
const serverCallsSeen = new Set();
const pendingOnServer = function (url) {
    const shape = String(url).split("?")[0];
    if (serverCallsSeen.size < 200 && !serverCallsSeen.has(shape)) {
        serverCallsSeen.add(shape);
        console.warn("[record-ssr] API call skipped on the server:", shape);
    }
    return new Promise(() => {});
}

const api = (function () {

    const makeUrl = function (pathName, searchParams) {
    // @pathName is the path, like /works
    // @searchParams can be in these formats: {foo: 42, bar: 43}, [["foo", 42], ["bar", 43]], ?foo=42&bar=43

        const params = new URLSearchParams(searchParams);

        // TODO shouldn't be on all URLs
        if (!pathName.startsWith("/autocomplete/")) {
            !params.get("per-page") && params.set("per-page", 10);
        }

        params.set("mailto", "ui@openalex.org");

        if (pathName.indexOf("/") !== 0) {
            pathName = "/" + pathName;
        }
        const baseAndPath = urlBase.api + pathName;
        const paramsStr = [...params.entries()]
            .filter(p => {
                return p[1];
            })
            .map(p => {
                return p[0] + "=" + p[1];
            })
            .join("&");

        const url = paramsStr ? [baseAndPath, paramsStr].join("?") : baseAndPath;
        //console.log("makeUrl OUTPUT", url);
        return url;
    }

    const getUrl = async function (url, config) {
        // config: extra axios options (e.g. {signal} to abort a stale
        // autocomplete request) merged over the standard auth headers.
        config = config ? {...axiosConfig(), ...config} : axiosConfig();
        
        if (!url.startsWith("http")) { 
            url = urlBase.api + url;
        }

        if (url.includes("filter=")) { // sdgs hack
            url = url.replace("sdgs/", "");
        }

        if (store.state.useV2) {
            url += url.includes('?') ? '&data-version=2' : '?data-version=2';
        }

        if (isServerRender) { return pendingOnServer(url); }

        const cachedResponse = getFromCache(url);
        if (cachedResponse) {
            //console.log("cache hit: " + url);
            return cachedResponse;
        }

        const res = await sendWithRefusalPolicy(() => axios.get(url, config));
        stockCache(url, res.data);
        return res.data;
    }

    // GET and the OQL POSTs share one refusal policy (withRefusalPolicy, oxjob #2002).
    const sendWithRefusalPolicy = function (send) {
        return withRefusalPolicy(send, {
            isSignedIn: () => !!store.state.user.id,
            onDailyLimitSignedOut: () => store.commit('showCreditLimitDialog'),
        });
    }

    const get =  async function (pathName, searchParams, config) {
        const url = makeUrl(pathName, searchParams);
        const resp = await getUrl(url, config);
        return resp;
    }

    const post = async function(url, data, config) {
        const resp = await axios.post(url, data, config);
        return resp;
    }

    // Some entities' API rows carry their name in a field other than
    // `display_name` (awards + locations: `title`). Resolve that field from
    // the request URL's leading path segment via entityConfigs.displayNameField
    // so it can be copied over at the API boundary — every downstream surface
    // (list items, table identity column, useHead, pickers) keeps assuming
    // `display_name`. Config-driven generalization of the old awards-only
    // hack (oxjob #852).
    const entityConfigForUrl = function (url) {
        try {
            const path = new URL(url, urlBase.api).pathname;
            const entityType = path.split('/').filter(Boolean)[0];
            if (!entityType) return null;
            return getEntityConfig(entityType) || null;
        } catch {
            return null;
        }
    };

    const displayNameFieldForUrl = function (url) {
        return entityConfigForUrl(url)?.displayNameField || null;
    };

    // Boundary normalization for entity result rows (oxjob #852):
    //  - displayNameField entities (awards + locations: `title`) get it copied
    //    to `display_name` so every downstream surface (list items, table
    //    identity column, useHead, pickers) keeps assuming `display_name`.
    //  - Component entities (locations) return BARE namespaced ids
    //    ("doi:10.1007/…") — no locations/ prefix, no URL — so nothing
    //    downstream (parseId, zoom links, identity column) recognizes them.
    //    Normalize to the full-URL convention every other entity uses.
    // Applied by getResultsList (classic list/SERP path) AND executeOqo (the
    // OQL/OQO table path — its rows come from POST /, so there's no URL to
    // derive the entity from; it passes the OQO's get_rows entity instead).
    const normalizeEntityRows = function (cfg, results) {
        const dnField = cfg?.displayNameField;
        const isComponent = cfg?.entityClass === 'component';
        if (!results || !(dnField || isComponent)) return results;
        return results.map(row => ({
            ...row,
            ...(dnField ? { display_name: row[dnField] || row.display_name } : {}),
            ...(isComponent && row.id && !String(row.id).includes('openalex.org/')
                ? { id: `https://openalex.org/${cfg.name}/${row.id}` }
                : {}),
        }));
    };

    const getResultsList = async function (url) {
        const ret = await getUrl(url);
        const cfg = entityConfigForUrl(url);
        if (ret.results) {
            ret.results = normalizeEntityRows(cfg, ret.results);
        }
        return ret;
    };

    const getEntity = async function (id) {
        const myUrl = makeUrl(id);
        const resp = await getUrl(myUrl);

        const dnField = displayNameFieldForUrl(myUrl);
        if (dnField && resp && resp[dnField] && !resp.display_name) {
            resp.display_name = resp[dnField];
        }

        return resp;
    };

    const getEntityFromCache = function(id) {
        const myUrl = makeUrl(id);
        return getFromCache(myUrl);
    };

    const getEntityDisplayName = async function (entityName, id) {
        const path = entityName + "/" + id;
        const myUrl = makeUrl(path, {select: "display_name"});
        const resp = await getUrl(myUrl);
        return resp.display_name;
    };

    const getFilterValueDisplayName = async function (filterKey, filterValue, entityType = "works") {
        const entityId = getFacetConfig(entityType, filterKey)?.entityToSelect;

        if (filterKey === "institutions.country_code" || filterKey === "country_code" || filterKey.endsWith(".country_code") || entityId === "countries") {
            return openAlexCountries.find(c => c.id.toLowerCase() === filterValue.toLowerCase())?.display_name;
        } else if (filterKey === "type") {
            // Work types: just capitalize and replace hyphens with spaces
            return filterValue.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
        } else if (filterKey === "sustainable_development_goals.id") {
            // SDG IDs can be short (e.g., "3") or full URLs (e.g., "https://metadata.un.org/sdg/3")
            const shortId = filterValue.replace(/.*\//, '').toLowerCase();
            return openAlexSdgs.find(c => {
                const cShortId = c.id.replace(/.*\//, '').toLowerCase();
                return cShortId === shortId || c.id.toLowerCase() === filterValue.toLowerCase();
            })?.display_name;
        } else if (filterKey === "language") {
            return ISO6391.getName(filterValue.toLowerCase());
        } else if (isListedInKey(filterKey)) {
            return listedInLabel(filterValue);
        } else if (entityId) {
            return await getEntityDisplayName(entityId, filterValue);
        } else {
            return filterValue;
        }
    }

    const getResultsCount = async function (entityType, filters) {
        const searchParams = {
            filter: filtersAsUrlStr(filters)
        }
        const url = makeUrl(entityType, searchParams)
        const ret = await getUrl(url)
        return ret.meta.count
    }

    // Turn a raw `group_by` array (from either the legacy GET or the POST-OQO
    // execute path) into the display filters the stats widgets render. Shared so
    // OXURL-mode (getGroups) and OQL-mode (getGroupsForOqo) produce identical
    // shapes — only the fetch channel differs.
    const buildGroupDisplayFilters = function (entityType, filterKey, groupByArray, hideUnknown) {
        const filteredGroups = (groupByArray || []).filter(g => {
            const keyIsNullish = g.key === "unknown" || g.key === null
            return !(keyIsNullish && hideUnknown)
        })

        const countSum = filteredGroups.reduce((a, g) => a + g.count, 0)

        const groupDisplayFilters = filteredGroups
            .map(group => {
                const groupKey = decodeURIComponent(group.key).replaceAll("https://metadata.un.org/sdg/", "") // namespace hack

                return createDisplayFilter(
                    entityType,
                    filterKey,
                    groupKey,
                    false,
                    // `listed_in`: the API echoes the bare id as key_display_name (#1205)
                    isListedInKey(filterKey) ? listedInLabel(groupKey) : group.key_display_name,
                    group.count,
                    group.count / countSum,
                )
            })

        // Year fields read most naturally in chronological order. The API returns
        // group_by buckets count-descending, which scrambles the years in the
        // "More…" picker (FilterSelectAddOption) so you can't see the data's range
        // at a glance (zd#8363). Sort year groups by year here — the card histogram
        // already depended on this ordering (it was sorted again in GroupBy.vue);
        // centralizing it keeps the histogram and the popup consistent.
        if (filterKey === "publication_year" || filterKey === "start_year") {
            groupDisplayFilters.sort((a, b) => parseInt(b.value) - parseInt(a.value))
        }

        return groupDisplayFilters
    }

    // Query-time join filters (oxjob #1526, `noGroupBy` facets: a work's source country
    // is looked up from the sources index, not stored) can't be grouped, so every value
    // picker that would group_by lists their values locally instead, without counts.
    const staticGroupsFor = function (entityType, filterKey) {
        const facetConfig = getFacetConfig(entityType, filterKey)
        if (!facetConfig?.noGroupBy) return null
        if (facetConfig.isCountry) {
            return openAlexCountries
                .filter(c => c.id !== "unknown")
                .map(c => createDisplayFilter(entityType, filterKey,
                    `https://openalex.org/countries/${c.id}`, false, c.display_name))
        }
        if (facetConfig.type === "boolean") {
            return [true, false].map(v => createDisplayFilter(entityType, filterKey, String(v), false,
                facetConfig.booleanValues?.[v ? 1 : 0] ?? String(v)))
        }
        return []
    }

    const getGroups = async function (entityType, filterKey, options) {
        const staticGroups = staticGroupsFor(entityType, filterKey)
        if (staticGroups) return staticGroups
        const myUrl = url.makeGroupByUrl(
            entityType,
            filterKey,
            options,
        )

        const respData = await getUrl(myUrl)
        return buildGroupDisplayFilters(entityType, filterKey, respData.group_by, options.hideUnknown)
    }

    // OQL-mode group-by (oxjob #528). The stats-widget counts must reflect the
    // CURRENT OQL query, which can be an arbitrary nested boolean tree that OXURL
    // `filter=` can't faithfully express — so we don't reconstruct a URL. Instead
    // we POST the canonical OQO (the same `executionOqo` the SERP runs) with a
    // single `group_by` dimension set to this facet's column, and read the
    // aggregation off the response. This is non-lossy by construction: the engine
    // aggregates over exactly the works the query matches.
    //
    // We strip the view chrome that's irrelevant to an aggregation (paging,
    // cursor, sort, projection) and raise per_page so we get the full bucket set
    // (matching the legacy makeGroupByUrl per_page=200). filterKey === the OQO
    // group_by column id (identity mapping; same string the legacy `group_by=`
    // param uses).
    // OQO group_by is a list of {column_id} dicts (NOT bare strings — the server
    // rejects strings with "string indices must be integers"). filterKey is the
    // column id (identity mapping with the legacy `group_by=` param).
    const buildGroupByOqo = function (oqo, filterKey) {
        // The incoming executionOqo is already pure "which rows" (#661 — sort,
        // select and paging live outside the OQO), so there's nothing to strip:
        // just point group_by at this facet's column. The bucket-set size
        // (per_page=200, matching the legacy makeGroupByUrl) rides as a sibling
        // request param, like all view params.
        // A pipeline query's calculations (#1536) stay out: the widget counts the
        // starting set's works, the same as for any other query, including a split's
        // listed values (`by institution in (...)`, #1744; startingSetOqo).
        return { ...startingSetOqo(oqo), group_by: [{ column_id: filterKey }] }
    }
    const GROUP_BY_PARAMS = { per_page: 200 }

    // The same aggregation as a GET link (#563 "View in API"): the API root
    // executes `?oqo=<json>` exactly like the POST body, so the link returns the
    // identical group_by the widget shows — no lossy OQL→OXURL translation.
    // (`?oql=…&group_by=…` does NOT work: the legacy group_by param is ignored
    // on the root endpoint and the response's group_by comes back empty.)
    const makeOqoGroupByUrl = function (oqo, filterKey) {
        const oqoJson = encodeURIComponent(JSON.stringify(buildGroupByOqo(oqo, filterKey)))
        return `${urlBase.api}/?oqo=${oqoJson}&per_page=${GROUP_BY_PARAMS.per_page}`
    }

    const getGroupsForOqo = async function (entityType, filterKey, oqo, options = {}) {
        if (!oqo || !filterKey) return []
        const staticGroups = staticGroupsFor(entityType, filterKey)
        if (staticGroups) return staticGroups
        const respData = await executeOqo(buildGroupByOqo(oqo, filterKey), GROUP_BY_PARAMS)
        return buildGroupDisplayFilters(entityType, filterKey, respData.group_by, options.hideUnknown)
    }

    const filterKeyFromAutocompleteResponse = function (result) {
        // this currently looks in result.filter_key but i think in the future we shoudl ignore that
        // and instead get the filterKey from the entity type, extracted from the ID?

        let filterKey
        if (result.filter_key === "id") filterKey = "ids.openalex"
        else if (result.filter_key === "topics.id") filterKey = "primary_topic.id"
        else filterKey = result.filter_key
        return filterKey
    }

    const getAutocompleteResponses = async function (entityType, filterKey, searchString, config = null) {
        //console.log("getAutocompleteResponses", entityType, filterKey, searchString)
        if (!filterKey && entityType !== "works") {
            filterKey = "ids.openalex"
        }

        const facetConfig = getFacetConfig(entityType, filterKey)

        // Countries aren't a native OpenAlex autocomplete entity:
        // /autocomplete/countries 404s ("OpenAlex ID format not recognized"),
        // so typing in a country filter surfaced no matches (zd#7567 — broke
        // only in Basic Filter Style, which routes typed input here; the
        // no-text state uses getGroups() and was fine). Search the static
        // country list locally instead, shaping each hit exactly like
        // getGroups() does (same /countries/<code> key → identical .value via
        // createFilterValue) so selection state and the stored filter match.
        if (facetConfig?.isCountry) {
            const term = (searchString || "").trim().toLowerCase()
            return openAlexCountries
                .filter(c => c.id !== "unknown")
                .filter(c => !term ||
                    c.display_name.toLowerCase().includes(term) ||
                    c.id.toLowerCase().includes(term))
                .slice(0, 10)
                .map(c => createDisplayFilter(
                    entityType,
                    filterKey,
                    `https://openalex.org/countries/${c.id}`,
                    false,
                    c.display_name,
                    c.works_count,
                    undefined,
                ))
        }

        const filterValueEntityId = facetConfig?.entityToSelect

        // edge autocomplete (oxjob #1529): filter-value lookups from the edge too, same row shape; null falls
        // through to today's endpoint
        const edgeResults = edgeOn() && filterValueEntityId && edgeSupports(filterValueEntityId)
            ? await edgeRows(filterValueEntityId, searchString || "")
            : null
        const resp = edgeResults
            ? { results: edgeResults }
            : await getUrl(url.makeAutocompleteUrl(filterValueEntityId, searchString), config)
        const suggestionFilters = resp.results
            .filter(r => !!r.id)
            .filter(r => r.entity_type !== "filter")
            .filter(r => !!r.display_name)
            .map(result => {
                const resultFilterKey = filterKey ?? filterKeyFromAutocompleteResponse(result) //
                // console.log("getAutocompleteResponses() map() resultFilterKey", resultFilterKey)

                const myFilter = createSimpleFilter(
                    entityType,
                    resultFilterKey,
                    result.id
                )
                // console.log("getAutocompleteResponses() map() myFilter", entityType, resultFilterKey, myFilter)

                const myHintVerb = myFilter.entityToSelect ? getEntityConfig(myFilter.entityToSelect)?.hintVerb : "-"
                const myHintString = result.hint ?? ""
                let tidyHintString = myHintString
                if (myFilter.entityToSelect === "topics") {
                    tidyHintString = tidyHintString
                        .replace("This cluster of papers ", "")
                        .replace("covers a ", "")
                        .replace("focuses on ", "")
                        .replace("explores the ", "")
                        .replace(/^the/, "")
                        .split(".")[0]

                    tidyHintString = tidyHintString.split(", including ")[0]
                    tidyHintString = tidyHintString.split(", emphasizing ")[0]
                    tidyHintString = tidyHintString.split(", particularly focusing ")[0]
                    tidyHintString = tidyHintString.split(", focusing on ")[0]
                    tidyHintString = tidyHintString.split(", particularly emphasizing ")[0]
                    tidyHintString = tidyHintString.split(", with a particular emphasis on ")[0]
                    tidyHintString = tidyHintString[0].toUpperCase() + tidyHintString.substring(1) + "."
                }

                const hint = tidyHintString ?
                    myHintVerb + " " + tidyHintString :
                    ""

                return {
                    ...myFilter,
                    displayValue: result.display_name,
                    worksCount: result.works_count,
                    count: result.works_count,
                    hint,
                    isFromAutocomplete: true,
                }

            })
        return suggestionFilters
    }

    // `oqo` (optional): the SERP's executionOqo when the caller lives in OQL mode
    // (oxjob #563). The legacy `filters` arg can't express an OQL query (it reads
    // the empty `?filter=` param there), so group-backed suggestion paths must
    // aggregate via getGroupsForOqo or the "More…" dialog shows UNFILTERED counts
    // while the widget itself shows filtered ones. Autocomplete-backed search
    // stays global — it always has been, in both modes.
    const getSuggestions = async function (entityType, filterKey, searchString, filters, oqo = null, config = null) {
        //console.log("getSuggestions", entityType, filterKey, searchString)
        // OQO has no within-group-by search param, so the searchString path
        // fetches the full (filtered) bucket set and substring-matches locally.
        const getGroupsViaOqo = async () => {
            const groups = await getGroupsForOqo(entityType, filterKey, oqo)
            if (!searchString) return groups
            const term = searchString.trim().toLowerCase()
            return groups.filter(g =>
                String(g.displayValue ?? g.value).toLowerCase().includes(term))
        }
        // Collections live in users-api, not in any elastic-api group_by / autocomplete.
        // Pull from the collections.store (the user's own and the public ones)
        // and filter to the current entity_type so a `works` SERP only sees
        // works collections.
        if (filterKey === 'collection') {
            const term = (searchString || '').trim().toLowerCase();
            const all = await collectionsForPickers();
            return all
                .filter(l => l.entity_type === entityType)
                .filter(l => {
                    if (!term) return true;
                    const name = (l.display_name || '').toLowerCase();
                    const desc = (l.description || '').toLowerCase();
                    return name.includes(term) || desc.includes(term);
                })
                .sort((a, b) =>
                    (a.display_name || '').localeCompare(b.display_name || '', undefined, { sensitivity: 'base' })
                )
                .map(l => ({ value: l.id, displayValue: l.display_name }));
        }
        // `listed_in`: a handful of values whose labels live client-side, so the
        // server's `?q=` (which matches the bare id) would miss "Conférence…".
        // Fetch every bucket and match label or id locally (#1205).
        if (isListedInKey(filterKey) && searchString && !oqo) {
            const term = searchString.trim().toLowerCase()
            const groups = await getGroups(entityType, filterKey, {filters})
            return groups.filter(g =>
                `${g.displayValue ?? ""} ${g.value ?? ""}`.toLowerCase().includes(term))
        }
        if (!searchString) {
            if (oqo) return await getGroupsViaOqo()
            return await getGroups(entityType, filterKey, {
                searchString,
                filters,
            })
        } else if (!filterKey) {
            return await getAutocompleteResponses(entityType, filterKey, searchString, config)
        } else {
            const myEntityId = getFacetConfig(entityType, filterKey)?.entityToSelect
            const isAutocompleteEndpointAvailable = myEntityId ? getEntityConfig(myEntityId)?.hasAutocomplete : false
            if (isAutocompleteEndpointAvailable && myEntityId) {
                return await getAutocompleteResponses(entityType, filterKey, searchString, config)
            } else {
                if (oqo) return await getGroupsViaOqo()
                return await getGroups(entityType, filterKey, {
                    searchString,
                    filters
                })
            }

        }
    };

    const getAutocomplete = async function(entityType, params, config) {
        // Edge autocomplete (oxjob #1529, edge autocomplete): every entity type but works, from the Cloudflare
        // edge in this same row shape; null (error, slow, no rows) falls through to today's endpoints below.
        if (edgeOn() && edgeSupports(entityType)) {
            const rows = await edgeRows(entityType, params?.q || "");
            if (rows) return rows;
        }
        // /autocomplete/<type> only exists for native types + keywords. Small
        // closed vocabs (countries, work-types, sdgs, …) are searched locally
        // over the fetched-once full list (oxjob #396; generalizes zd#7567).
        if (isSmallVocabType(entityType)) {
            return searchVocab(entityType, params?.q);
        }
        // awards: no /autocomplete endpoint and far too many to fetch — fall
        // back to the list endpoint's full-text search (stemmed, not prefix,
        // but it finds funder/program names like "nih").
        if (entityType === "awards") {
            const resp = await get(`/awards`, {
                search: params?.q || "",
                "per-page": 25,
                select: "id,display_name,works_count",
            }, config);
            return resp.results;
        }
        const response = await get(`/autocomplete/${entityType}`, params, config);
        //console.log("getAutocomplete", response);
        return response.results;
    };

    // Front-page basic-search autocomplete, in ONE request. Hits an ad-hoc,
    // GUI-only combo endpoint in openalex-api-proxy (/gui/autocomplete) that
    // fans out to author/institution search + keyword/work autocomplete
    // server-side and returns { authors, institutions, keywords, works } (each a
    // raw results array). We used to fire those four as parallel calls per
    // keystroke-pause; anonymous clients are capped at 10 req/s, so fast typing
    // tripped the per-second throttle and the resulting 429 popped the daily-
    // credit-limit modal mid-typing (even though no credits were spent). One
    // request per pause keeps us well under the cap. `includeWorks=false` (short
    // queries) tells the endpoint to skip the work-title lookup. `config` carries
    // the abort {signal} so a stale keystroke's request is cancelled.
    const getFrontpageAutocomplete = async function(query, { includeWorks = true, signal } = {}) {
        const params = new URLSearchParams({ q: query });
        if (!includeWorks) params.set("works", "0");
        const resp = await getUrl(`/gui/autocomplete?${params.toString()}`, signal ? { signal } : undefined);
        return {
            authors: resp.authors || [],
            institutions: resp.institutions || [],
            keywords: resp.keywords || [],
            works: resp.works || [],
        };
    };

    // Cross-type collection filter (oxjob #273): the user's collections that can
    // be used as a VALUE of `filterKey` on this SERP — i.e. collections whose
    // entity_type matches the field's selected entity type. Pulled from the
    // collections.store (the user's own, cap 100, and the public ones); type-match + search
    // + sort are pure (see collectionFilter.js). Returns [] for fields with no
    // matching collections, which lets callers self-scope ("no collections → no
    // Collections tab"). The dedicated `collection` field keeps its own picker
    // path in getSuggestions; this is for every OTHER selectEntity field.
    const getCollectionSuggestionsForField = async function (entityType, filterKey, searchString) {
        const selectType = collectionMatchType(entityType, filterKey);
        if (!selectType) return [];
        return filterCollectionsForField(await collectionsForPickers(), selectType, searchString);
    };

    // The user's own collections, then the public ones they don't own (oxjob #1532).
    // Each keeps its `access`, so pickers can tag it Public, Private or Shared by link.
    const collectionsForPickers = async function () {
        const loads = [store.dispatch('collections/fetchAllPublic')];
        if (!store.state.collections?.loaded && !store.state.collections?.loading) {
            loads.push(store.dispatch('collections/fetchAll'));
        }
        await Promise.all(loads.map(p => p.catch(() => [])));
        const mine = store.state.collections?.collections || [];
        const mineIds = new Set(mine.map(c => c.id));
        const publicOnes = (store.state.collections?.publicCollections || []).filter(c => !mineIds.has(c.id));
        return [...mine, ...publicOnes];
    };

    const createExport = async function(query, email) {
        // Initiates a data export to CSV via the user API
        // The query object should contain filter params
        const params = new URLSearchParams();
        if (query.filter) params.set('filter', query.filter);
        if (query.format) params.set('format', query.format);
        if (email) params.set('email', email);
        
        const url = `${urlBase.userApi}/export/works?${params.toString()}`;
        const resp = await axios.get(url, axiosConfig({userAuth: true}));
        return resp;
    }

    // Session cache for the `{oql}`-keyed /query translate (oxjob #562). A given OQL
    // string always translates to the same payload, and the same translate is fetched
    // from several places (the builder's mount seed, SerpInputContainer's grid probe).
    // Mode-tab switches remount the builder and re-paid this round-trip every time,
    // which read as "the query re-ran". Stores the in-flight promise so concurrent
    // callers share one request (a rejection evicts, so a failure can retry); hands
    // each caller a deep CLONE — the builder mutates the returned oql_render_v2 tree
    // in place, so a shared object would poison later mounts. The oqo/oxurl translate
    // paths are NOT cached: their keys churn on every edit render.
    const _oqlTranslateCache = new Map();

    const getQuery = async function(params) {
        // Translate a query to all formats: { oxurl, oql, oql_render, oqo, validation }.
        // Addresses the /query resource by one representation (oxurl, oqo, or oql); see oxjob #372.
        // params: { entity_type, filter, sort, query, oqo, oql }
        //   query = a bag of any extra oxurl querystring params (search, search.*, …),
        //   so the OQL render reflects the WHOLE query, not just filter/sort.
        let path, body, cacheKey = null;
        if (params.oql != null) {
            // OQL goes into the URL whitespace-collapsed: the parser is whitespace-
            // blind, so the pretty-print layout is needless in a URL — and a raw
            // newline 404s this GET route (Flask path segments don't match \n).
            // oqlForUrl is the single shared serializer (oxjob #373 Phase 2,
            // generalizing the #428 flatten to also drop indentation). Imported
            // from the leaf @/oqlSerialize, NOT via the `url` object — api.js ⇄
            // url.js are circular, and `url` is initialized too late for this
            // mount-time call (TDZ). See @/oqlSerialize for the full why.
            const oql = oqlForUrl(params.oql);
            path = `oql/${encodeURIComponent(oql)}`;
            body = { oql };
            cacheKey = oql;
            const cached = _oqlTranslateCache.get(cacheKey);
            if (cached) return structuredClone(await cached);
        } else if (params.oqo) {
            const oqoStr = typeof params.oqo === 'string' ? params.oqo : JSON.stringify(params.oqo);
            path = `oqo/${encodeURIComponent(oqoStr)}`;
            body = { oqo: params.oqo };
        } else {
            const entityType = params.entity_type || 'works';
            const queryParams = new URLSearchParams();
            if (params.filter) queryParams.set('filter', params.filter);
            if (params.sort) queryParams.set('sort', params.sort);
            if (params.query && typeof params.query === 'object') {
                for (const [k, v] of Object.entries(params.query)) {
                    if (v != null && v !== '') queryParams.set(k, v);
                }
            }
            const qs = queryParams.toString();
            const oxurlValue = qs ? `${entityType}?${qs}` : entityType;
            path = `oxurl/${encodeURIComponent(oxurlValue)}`;
            body = { oxurl: oxurlValue };
        }
        const url = `${urlBase.api}/query/${path}`;
        // The translate routes carry the WHOLE query in the URL path; the origin's
        // gunicorn caps the request line at 8190 bytes (enforced at the proxy edge).
        // A large query overflows that and 400s — e.g. the no-code builder re-renders
        // through `/query/oqo/<json>` after every structural edit, and a ~100-term
        // Boolean OQO encodes to ~17 KB, so the edit was silently dropped (negate /
        // join / operator / remove all no-op on big queries). POST /query takes the
        // same query in a body (no length cap) and returns the identical payload, so
        // fall back to it when the GET request line would be over the limit. (#428.)
        const fetchTranslate = (async () => {
            if (url.length > 8000) {
                const resp = await axios.post(`${urlBase.api}/query`, body, axiosConfig());
                return resp.data;
            }
            const resp = await axios.get(url, axiosConfig());
            return resp.data;
        })();
        if (cacheKey != null) {
            _oqlTranslateCache.set(cacheKey, fetchTranslate);
            fetchTranslate.catch(() => _oqlTranslateCache.delete(cacheKey));
            return structuredClone(await fetchTranslate);
        }
        return fetchTranslate;
    }

    // The orders one query can take, for the sort menu (oxjob #1658): GET /query/orders
    // (free, parse-only) -> {order, order_default, options: [{words, sort_by, default?}]};
    // POST past the request-line cap, like getQuery. Cached per OQL; a failure evicts, so
    // the menu can ask again.
    const _ordersCache = new Map();
    const getQueryOrders = async function(oql) {
        const q = oqlForUrl(oql);
        if (!_ordersCache.has(q)) {
            const url = `${urlBase.api}/query/orders?q=${encodeURIComponent(q)}`;
            const p = (url.length > 8000
                ? axios.post(`${urlBase.api}/query/orders`, { q }, axiosConfig())
                : axios.get(url, axiosConfig()))
                .then((resp) => resp.data);
            p.catch(() => _ordersCache.delete(q));
            _ordersCache.set(q, p);
        }
        return _ordersCache.get(q);
    }

    const executeOql = async function(oql, params = {}) {
        // Submit an OQL string to be parsed → executed → results (oxjob #373).
        // POSTs to the API root (the execute surface shipped in #372), which has
        // no request-line cap — so even long/expressive OQL runs. The response is
        // the normal {meta, results, group_by} envelope PLUS the private
        // meta.x_query = {oql, oqo, url} triple the client rehydrates from.
        // Throws on a 4xx with a structured {validation: {errors: [...]}} body.
        // `params` are sibling view params (e.g. a pipeline query's `sort`, #1536).
        const url = `${urlBase.api}/?mailto=ui@openalex.org`;
        const resp = await sendWithRefusalPolicy(() => axios.post(url, { oql, ...params }, axiosConfig()));
        // Same boundary normalization as getResultsList/executeOqo (oxjob
        // #852) — the /q?oql= route fetches through here, and locations rows
        // otherwise render a blank identity column + unlinkable rows.
        const data = resp.data;
        const entityType = data?.meta?.x_query?.oqo?.get_rows;
        if (data?.results && entityType) {
            data.results = normalizeEntityRows(getEntityConfig(entityType), data.results);
        }
        return data;
    }

    const executeOqo = async function(oqo, params = {}) {
        // Execute a canonical OQO directly (oxjob #464 pillar A). Same execute
        // surface as executeOql (POST /, no request-line cap — the OQO can be an
        // arbitrarily large boolean tree), but the query is already structured so
        // the server skips the OQL parse. Response is the normal {meta, results,
        // group_by} envelope with meta.x_query = {oql, oqo, url} echoing the
        // server-canonical query.
        //
        // #661 query/view split: the OQO is pure "which rows" (get_rows, corpus,
        // filter_rows, group_by, sample/seed). View params — sort/select/page/
        // per_page/cursor, classic wire syntax ("sort": "cited_by_count:desc",
        // "select": "id,doi") — ride as SIBLING body keys next to `oqo`. The
        // server's strict-body allowlist accepts exactly these; a sibling wins
        // over any (transitional) embedded value. Throws on a 4xx with a
        // structured {validation: {errors: [...]}} body, like executeOql.
        const url = `${urlBase.api}/?mailto=ui@openalex.org`;
        const resp = await sendWithRefusalPolicy(() => axios.post(url, { oqo, ...params }, axiosConfig()));
        // Same boundary normalization as getResultsList (oxjob #852): OQO rows
        // for displayNameField/component entities (locations: title, bare ids)
        // otherwise render a blank identity column + unlinkable rows in the
        // OQL results table. Entity comes from the server-canonical echo,
        // falling back to the submitted OQO.
        const data = resp.data;
        const entityType = data?.meta?.x_query?.oqo?.get_rows || oqo?.get_rows;
        if (data?.results && entityType) {
            const cfg = getEntityConfig(entityType);
            data.results = normalizeEntityRows(cfg, data.results);
        }
        return data;
    }

    return {
        getEntityDisplayName,
        getFilterValueDisplayName,
        getUrl,
        getResultsList,
        getResultsCount,
        getEntity,
        getEntityFromCache,
        get,
        getAutocompleteResponses,
        getGroups,
        getGroupsForOqo,
        makeOqoGroupByUrl,
        getSuggestions,
        getCollectionSuggestionsForField,
        post,
        getAutocomplete,
        getFrontpageAutocomplete,
        makeUrl,
        createExport,
        getQuery,
        getQueryOrders,
        executeOql,
        executeOqo,
    }
})();


export {
    api,
}